import {
  CSSProperties,
  PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  CLOSE_THRESHOLD,
  DECIDE_THRESHOLD,
  DOUBLE_TAP_DELAY,
  DOUBLE_TAP_ZOOM,
  EDGE_RESISTANCE,
  MIN_SCALE,
  RESISTANCE,
  SNAP_DURATION,
  SWIPE_DURATION,
  SWIPE_THRESHOLD,
  ZOOM_DURATION,
} from './carousel.constants';
import { GestureState, Point } from './carousel.types';
import {
  clampOrigin,
  getDist,
  getMaxScale,
  getMid,
  getSlide,
  getSwipePanel,
} from './carousel.utils';

type Options = {
  index: number;
  total: number;
  onSlideChange?: () => void;
  onNavigate: (dir: -1 | 1) => void;
  onSwipeDown?: () => void;
  onSwipeUp?: () => void;
  onTap?: () => void;
  keyboard?: boolean;
};

export const useCarousel = ({
  index,
  total,
  onSlideChange,
  onNavigate,
  onSwipeDown,
  onSwipeUp,
  onTap,
  keyboard = true,
}: Options) => {
  const ref = useRef<HTMLDivElement>(null);
  const [delta, setDelta] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const gesture = useRef<GestureState>('IDLE');

  const pointers = useRef<Map<number, Point>>(new Map());
  const startPoint = useRef<Point>({ x: 0, y: 0 });

  const startDelta = useRef(0);

  const panelY = useRef(0);

  const scale = useRef(1);
  const origin = useRef<Point>({ x: 0, y: 0 });
  const originOnPanStart = useRef<Point>({ x: 0, y: 0 });
  const lastPinchDist = useRef<number | null>(null);
  const lastPinchMid = useRef<Point | null>(null);

  const lastClick = useRef(0);

  const pinchStartDist = useRef<number>(0);
  const pinchStartScale = useRef<number>(1);
  const pinchStartMid = useRef<Point>({ x: 0, y: 0 });
  const pinchStartOrigin = useRef<Point>({ x: 0, y: 0 });
  const elCenter = useRef<Point>({ x: 0, y: 0 });

  const applyZoom = (slideEl: HTMLElement, animated = false) => {
    slideEl.style.transition = animated
      ? `transform ${ZOOM_DURATION}ms ease`
      : 'none';
    slideEl.style.transform = `translate(${origin.current.x}px, ${origin.current.y}px) scale(${scale.current})`;
  };

  const resetZoom = useCallback((slideEl: HTMLElement, animated = false) => {
    scale.current = MIN_SCALE;
    origin.current = { x: 0, y: 0 };
    applyZoom(slideEl, animated);

    if (animated) {
      setTimeout(() => {
        slideEl.style.transition = 'none';
      }, ZOOM_DURATION);
    }
  }, []);

  const zoomTo = useCallback(
    (
      slideEl: HTMLElement,
      newScale: number,
      centerX: number,
      centerY: number,
      animated = false
    ) => {
      const rect = slideEl.getBoundingClientRect();
      const offsetX = centerX - (rect.left + rect.width / 2);
      const offsetY = centerY - (rect.top + rect.height / 2);
      const ratio = newScale / scale.current;
      const rawX = (origin.current.x + offsetX) * ratio - offsetX;
      const rawY = (origin.current.y + offsetY) * ratio - offsetY;

      scale.current = newScale;
      origin.current = clampOrigin(slideEl, -rawX, -rawY, newScale);
      applyZoom(slideEl, animated);

      if (animated) {
        setTimeout(() => {
          slideEl.style.transition = 'none';
        }, ZOOM_DURATION);
      }
    },
    []
  );

  const isZoomed = () => scale.current > 1.01;

  const applyPanel = (el: HTMLElement, y: number, animated = false) => {
    el.style.transition = animated
      ? `transform ${SNAP_DURATION}ms ease, opacity ${SNAP_DURATION}ms ease`
      : 'none';
    el.style.transform = `translateY(${y}px)`;
    el.style.opacity = y > 0 ? `${1 - y / 400}` : '1';
  };

  const resetPanel = useCallback((el: HTMLElement, animated = true) => {
    panelY.current = 0;
    applyPanel(el, 0, animated);

    if (animated) {
      setTimeout(() => {
        el.style.transition = 'none';
      }, SNAP_DURATION);
    }
  }, []);

  useEffect(() => {
    const el = ref.current;

    if (!el) {
      return;
    }

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) {
        return;
      }

      const slideEl = getSlide(el, index);

      if (!slideEl) {
        return;
      }

      const zoomingOut = e.deltaY > 0;

      e.preventDefault();

      const delta = zoomingOut ? 0.9 : 1.1;
      const maxScale = getMaxScale(slideEl);
      const newScale = Math.max(
        MIN_SCALE,
        Math.min(maxScale, scale.current * delta)
      );

      if (newScale <= MIN_SCALE) {
        resetZoom(slideEl, true);

        return;
      }

      const savedTransform = slideEl.style.transform;
      slideEl.style.transform = 'none';
      const rect = slideEl.getBoundingClientRect();
      slideEl.style.transform = savedTransform;

      const offsetX = e.clientX - (rect.left + rect.width / 2);
      const offsetY = e.clientY - (rect.top + rect.height / 2);
      const ratio = newScale / scale.current;

      const newOriginX = offsetX * (1 - ratio) + origin.current.x * ratio;
      const newOriginY = offsetY * (1 - ratio) + origin.current.y * ratio;

      scale.current = newScale;
      origin.current = clampOrigin(slideEl, newOriginX, newOriginY, newScale);
      applyZoom(slideEl);
    };

    el.addEventListener('wheel', onWheel, { passive: false });

    return () => el.removeEventListener('wheel', onWheel);
  }, [index, resetZoom, zoomTo]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const slideEl = getSlide(ref.current, index);

    if (slideEl) {
      resetZoom(slideEl);
    }
    scale.current = MIN_SCALE;
    origin.current = { x: 0, y: 0 };
  }, [index, resetZoom]);

  useEffect(() => {
    if (!keyboard) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        onNavigate(-1);
      }

      if (e.key === 'ArrowRight') {
        onNavigate(1);
      }
    };
    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [keyboard, onNavigate]);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const size = pointers.current.size;

      if (size === 2) {
        gesture.current = 'PINCHING';
        setIsDragging(false);
        setDelta(0);

        const pts = Array.from(pointers.current.values());
        const mid = getMid(pts[0], pts[1]);
        const dist = getDist(pts[0], pts[1]);

        pinchStartDist.current = dist;
        pinchStartScale.current = scale.current;
        pinchStartMid.current = mid;
        pinchStartOrigin.current = { ...origin.current };

        const slideEl = getSlide(ref.current!, index);

        if (slideEl) {
          const savedTransform = slideEl.style.transform;
          slideEl.style.transition = 'none';
          slideEl.style.transform = 'none';
          const rect = slideEl.getBoundingClientRect();
          slideEl.style.transform = savedTransform;

          elCenter.current = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          };
        }

        lastPinchDist.current = dist;
        lastPinchMid.current = mid;

        const ids = Array.from(pointers.current.keys());
        ids.forEach((id) => {
          try {
            e.currentTarget.setPointerCapture(id);
          } catch {}
        });
      } else if (size === 1) {
        gesture.current = 'DECIDING';
        startPoint.current = { x: e.clientX, y: e.clientY };
        startDelta.current = 0;
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [index]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (!ref.current) {
        return;
      }

      const dx = e.clientX - startPoint.current.x;
      const dy = e.clientY - startPoint.current.y;

      if (gesture.current === 'PINCHING') {
        if (pointers.current.size < 2) {
          return;
        }

        const pts = Array.from(pointers.current.values());
        const dist = getDist(pts[0], pts[1]);
        const mid = getMid(pts[0], pts[1]);
        const slideEl = getSlide(ref.current, index);

        if (!slideEl) {
          return;
        }

        const totalRatio = dist / pinchStartDist.current;
        const maxScale = getMaxScale(slideEl);
        const newScale = Math.max(
          MIN_SCALE,
          Math.min(maxScale, pinchStartScale.current * totalRatio)
        );
        const scaleRatio = newScale / pinchStartScale.current;

        const dmx = mid.x - pinchStartMid.current.x;
        const dmy = mid.y - pinchStartMid.current.y;

        const px = pinchStartMid.current.x - elCenter.current.x;
        const py = pinchStartMid.current.y - elCenter.current.y;

        const newOriginX =
          px * (1 - scaleRatio) + pinchStartOrigin.current.x * scaleRatio + dmx;
        const newOriginY =
          py * (1 - scaleRatio) + pinchStartOrigin.current.y * scaleRatio + dmy;

        scale.current = newScale;
        origin.current = clampOrigin(slideEl, newOriginX, newOriginY, newScale);
        applyZoom(slideEl);

        return;
      }

      if (gesture.current === 'PANNING') {
        const slideEl = getSlide(ref.current, index);

        if (!slideEl) {
          return;
        }
        origin.current = clampOrigin(
          slideEl,
          originOnPanStart.current.x + dx,
          originOnPanStart.current.y + dy,
          scale.current
        );
        applyZoom(slideEl);

        return;
      }

      if (gesture.current === 'DECIDING') {
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);

        if (absDx < DECIDE_THRESHOLD && absDy < DECIDE_THRESHOLD) {
          return;
        }

        if (isZoomed()) {
          gesture.current = 'PANNING';
          originOnPanStart.current = { ...origin.current };

          return;
        }

        if (absDy > absDx * 1.5) {
          gesture.current = 'SWIPING_V';
        } else {
          gesture.current = 'SWIPING_H';
          setIsDragging(true);
        }
      }

      if (gesture.current === 'SWIPING_H') {
        const atStart = index === 0 && dx > 0;
        const atEnd = index === total - 1 && dx < 0;
        setDelta(atStart || atEnd ? dx * EDGE_RESISTANCE : dx);

        return;
      }

      if (gesture.current === 'SWIPING_V') {
        const panelEl = getSwipePanel(ref.current);

        if (!panelEl) {
          return;
        }
        panelY.current = dy * RESISTANCE;
        applyPanel(panelEl, panelY.current);
      }
    },
    [index, total]
  );

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      pointers.current.delete(e.pointerId);

      if (!ref.current) {
        return;
      }

      const dx = e.clientX - startPoint.current.x;
      const w = ref.current.offsetWidth;

      switch (gesture.current) {
        case 'SWIPING_H': {
          setIsDragging(false);
          setDelta(0);

          if (dx < -(w * SWIPE_THRESHOLD) && index < total - 1) {
            onNavigate(1);
            onSlideChange?.();
          } else if (dx > w * SWIPE_THRESHOLD && index > 0) {
            onNavigate(-1);
            onSlideChange?.();
          }
          break;
        }

        case 'SWIPING_V': {
          const panelEl = getSwipePanel(ref.current);

          if (panelEl) {
            if (panelY.current > CLOSE_THRESHOLD) {
              onSwipeDown?.();
              resetPanel(panelEl, false);
            } else if (panelY.current < -CLOSE_THRESHOLD) {
              onSwipeUp?.();
              resetPanel(panelEl, false);
            } else {
              resetPanel(panelEl, true);
            }
          }
          break;
        }

        case 'PINCHING': {
          if (scale.current < 1.1) {
            const slideEl = getSlide(ref.current, index);

            if (slideEl) {
              resetZoom(slideEl, true);
            }
          }
          break;
        }
      }

      if (pointers.current.size === 0) {
        gesture.current = 'IDLE';
        lastPinchDist.current = null;
        lastPinchMid.current = null;
      }
    },
    [index, total, onNavigate, onSwipeDown, resetPanel, onSwipeUp, resetZoom]
  );

  const onPointerCancel = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      pointers.current.delete(e.pointerId);

      if (!ref.current) {
        return;
      }

      setIsDragging(false);
      setDelta(0);

      const panelEl = getSwipePanel(ref.current);

      if (panelEl) {
        resetPanel(panelEl, true);
      }

      gesture.current = 'IDLE';
      lastPinchDist.current = null;
      lastPinchMid.current = null;
    },
    [resetPanel]
  );

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!ref.current) {
        return;
      }

      const slideEl = getSlide(ref.current, index);

      if (!slideEl) {
        return;
      }

      const dx = Math.abs(e.clientX - startPoint.current.x);
      const dy = Math.abs(e.clientY - startPoint.current.y);

      if (dx > 5 || dy > 5) {
        return;
      }

      const now = Date.now();

      if (now - lastClick.current < DOUBLE_TAP_DELAY) {
        // Double Tap
        lastClick.current = 0;
        const slideEl = getSlide(ref.current, index);

        if (!slideEl) {
          return;
        }

        if (isZoomed()) {
          resetZoom(slideEl, true);
        } else {
          zoomTo(slideEl, DOUBLE_TAP_ZOOM, e.clientX, e.clientY, true);
        }

        return;
      }

      lastClick.current = now;

      setTimeout(() => {
        if (lastClick.current === 0) {
          return;
        }

        onTap?.();
      }, DOUBLE_TAP_DELAY);
    },
    [index, onTap, resetZoom, zoomTo]
  );

  const trackStyle: CSSProperties = {
    transform: `translateX(calc(-${(index / total) * 100}% + ${delta}px))`,
    transition: isDragging
      ? 'none'
      : `transform ${SWIPE_DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
  };

  return {
    ref,
    trackStyle,
    pointerHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onClick,
    },
  };
};
